import { useEffect, useState, useCallback } from "react";
import { LogOut, Package, Mail, Euro, Clock, RefreshCw, Loader2, Download, Truck, FileText } from "lucide-react";
import { toast } from "sonner";
import { adminStats, adminOrders, adminContacts, adminRevenueMonthly, downloadInvoice, downloadLabel, downloadPackingSlip, regenerateLabel, clearToken, fmtEuro, fmtDate } from "@/lib/adminApi";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import RevenueChart from "@/components/admin/RevenueChart";
import HubManager from "@/components/admin/HubManager";
import SlidesManager from "@/components/admin/SlidesManager";
import KoppelingenTab from "@/components/admin/KoppelingenTab";

const PRODUCT_NAMES = {
  nfc_google: "Google Reviews",
  nfc_facebook: "Facebook",
  nfc_instagram: "Instagram",
  nfc_complete: "Complete Package",
  maintenance_basic_monthly: "Onderhoud Basic",
  maintenance_pro_monthly: "Onderhoud Pro",
  display_stand: "Display",
};

const itemsLabel = (items = []) =>
  items.map((i) => `${PRODUCT_NAMES[i.lookup_key] || i.lookup_key} ×${i.quantity}`).join(", ");

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <div className="flex items-center gap-3">
      <div className="grid h-10 w-10 place-items-center rounded-lg bg-[#0a5a3c]/10">
        <Icon className="h-5 w-5 text-[#0a5a3c]" />
      </div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
        <p className="text-2xl font-bold text-[#0a2540]">{value}</p>
      </div>
    </div>
  </div>
);

const InvoiceButton = ({ order }) => {
  const [busy, setBusy] = useState(false);
  const downloadable = order.mode === "payment" && order.payment_status === "paid";
  if (!downloadable) return <span className="text-slate-300">-</span>;
  const handle = async () => {
    setBusy(true);
    try {
      await downloadInvoice(order.session_id, `factuur-${order.invoice_number || order.session_id}.pdf`);
    } catch {
      toast.error("Kon factuur niet downloaden");
    }
    setBusy(false);
  };
  return (
    <button onClick={handle} disabled={busy} data-testid={`invoice-download-${order.session_id}`}
      className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-[#0a5a3c] hover:bg-[#0a5a3c]/5 disabled:opacity-50">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PDF
    </button>
  );
};

const readBlobDetail = async (err) => {
  try {
    const txt = await err.response.data.text();
    return JSON.parse(txt).detail;
  } catch {
    return null;
  }
};

const LabelButton = ({ order, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const downloadable = order.mode === "payment" && order.payment_status === "paid";
  if (!downloadable) return <span className="text-slate-300">-</span>;
  const handle = async () => {
    setBusy(true);
    try {
      await downloadLabel(order.session_id, `postnl-${order.postnl_barcode || order.session_id}.pdf`);
    } catch (err) {
      const detail = await readBlobDetail(err);
      toast.error(detail || "Kon verzendlabel niet aanmaken");
    }
    setBusy(false);
  };
  const regen = async () => {
    if (!window.confirm("Label opnieuw genereren? Het vorige label vervalt (handig na de overstap naar PostNL-productie).")) return;
    setBusy(true);
    try {
      const { barcode } = await regenerateLabel(order.session_id);
      toast.success(`Nieuw label aangemaakt (${barcode})`);
      await downloadLabel(order.session_id, `postnl-${barcode}.pdf`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon label niet opnieuw genereren");
    }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-1.5">
      <button onClick={handle} disabled={busy} data-testid={`label-download-${order.session_id}`}
        className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-[#0a2540] hover:bg-[#0a2540]/5 disabled:opacity-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Truck className="h-3.5 w-3.5" />} Label
      </button>
      {order.postnl_barcode && (
        <button onClick={regen} disabled={busy} title="Label opnieuw genereren" data-testid={`label-regen-${order.session_id}`}
          className="grid h-7 w-7 place-items-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

const PackingSlipButton = ({ order }) => {
  const [busy, setBusy] = useState(false);
  const downloadable = order.mode === "payment" && order.payment_status === "paid";
  if (!downloadable) return <span className="text-slate-300">-</span>;
  const handle = async () => {
    setBusy(true);
    try {
      await downloadPackingSlip(order.session_id, `verzendbon-${order.invoice_number || order.session_id}.pdf`);
    } catch (err) {
      const detail = await readBlobDetail(err);
      toast.error(detail || "Kon verzendbon niet downloaden");
    }
    setBusy(false);
  };
  return (
    <button onClick={handle} disabled={busy} data-testid={`packing-slip-download-${order.session_id}`}
      className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-[#0a2540] hover:bg-[#0a2540]/5 disabled:opacity-50">
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} Verzendbon
    </button>
  );
};

const AdminDashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => { clearToken(); onLogout(); }, [onLogout]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o, c, m] = await Promise.all([adminStats(), adminOrders(), adminContacts(), adminRevenueMonthly()]);
      setStats(s);
      setOrders(o.orders || []);
      setContacts(c.contacts || []);
      setMonthly(m.monthly || []);
    } catch (err) {
      if (err.response?.status === 401) { logout(); return; }
      toast.error("Kon gegevens niet laden");
    }
    setLoading(false);
  }, [logout]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <div className="flex items-center gap-2">
              <img src="/logo_aponiqo_dark.png" alt="aponiqo" className="h-6 w-auto" />
              <span className="text-lg font-bold text-[#0a2540]">Admin</span>
            </div>
            <p className="mt-0.5 text-xs text-slate-500">{user?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh"
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
              <RefreshCw className="h-4 w-4" /> Vernieuwen
            </button>
            <button onClick={logout} data-testid="admin-logout"
              className="flex items-center gap-1.5 rounded-lg bg-[#0a2540] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0a1f38]">
              <LogOut className="h-4 w-4" /> Uitloggen
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        {loading && !stats ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0a5a3c]" /></div>
        ) : (
          <>
            <RevenueChart data={monthly} />
            <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard icon={Euro} label="Omzet (betaald)" value={fmtEuro(stats?.revenue)} testid="stat-revenue" />
              <StatCard icon={Package} label="Betaalde orders" value={stats?.paid_orders ?? 0} testid="stat-paid" />
              <StatCard icon={Clock} label="Openstaand" value={stats?.pending_orders ?? 0} testid="stat-pending" />
              <StatCard icon={Mail} label="Aanvragen" value={stats?.contacts ?? 0} testid="stat-contacts" />
            </div>

            <Tabs defaultValue="orders">
              <TabsList data-testid="admin-tabs">
                <TabsTrigger value="orders" data-testid="tab-orders">Bestellingen</TabsTrigger>
                <TabsTrigger value="contacts" data-testid="tab-contacts">Aanvragen</TabsTrigger>
                <TabsTrigger value="hub" data-testid="tab-hub">aponiqo Hub</TabsTrigger>
                <TabsTrigger value="slides" data-testid="tab-slides">Slideshow data</TabsTrigger>
                <TabsTrigger value="koppelingen" data-testid="tab-koppelingen">Koppelingen</TabsTrigger>
              </TabsList>

              <TabsContent value="orders">
                <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                  <table className="w-full text-sm" data-testid="orders-table">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Datum</th>
                        <th className="px-4 py-3">Factuurnr.</th>
                        <th className="px-4 py-3">Apotheek</th>
                        <th className="px-4 py-3">E-mail</th>
                        <th className="px-4 py-3">Items</th>
                        <th className="px-4 py-3 text-right">Totaal</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Factuur</th>
                        <th className="px-4 py-3">Verzendbon</th>
                        <th className="px-4 py-3">Label</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {orders.length === 0 ? (
                        <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">Nog geen bestellingen</td></tr>
                      ) : orders.map((o) => (
                        <tr key={o.session_id} data-testid={`order-row-${o.session_id}`} className="hover:bg-slate-50">
                          <td className="whitespace-nowrap px-4 py-3 text-slate-600">{fmtDate(o.created_at)}</td>
                          <td className="px-4 py-3 font-medium text-[#0a2540]">{o.invoice_number || "-"}</td>
                          <td className="px-4 py-3">{o.pharmacy_name || "-"}</td>
                          <td className="px-4 py-3 text-slate-600">{o.customer_email || "-"}</td>
                          <td className="max-w-xs px-4 py-3 text-slate-600">{itemsLabel(o.items)}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-[#0a2540]">{fmtEuro(o.amount)}</td>
                          <td className="px-4 py-3">
                            <Badge variant={o.payment_status === "paid" ? "default" : "secondary"}
                              className={o.payment_status === "paid" ? "bg-[#0a5a3c] hover:bg-[#0a5a3c]" : ""}>
                              {o.payment_status === "paid" ? "Betaald" : "Open"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3"><InvoiceButton order={o} /></td>
                          <td className="px-4 py-3"><PackingSlipButton order={o} /></td>
                          <td className="px-4 py-3"><LabelButton order={o} onChanged={load} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              <TabsContent value="contacts">
                <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                  <table className="w-full text-sm" data-testid="contacts-table">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Datum</th>
                        <th className="px-4 py-3">Naam</th>
                        <th className="px-4 py-3">Apotheek</th>
                        <th className="px-4 py-3">E-mail</th>
                        <th className="px-4 py-3">Telefoon</th>
                        <th className="px-4 py-3">Bericht</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {contacts.length === 0 ? (
                        <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Nog geen aanvragen</td></tr>
                      ) : contacts.map((c, idx) => (
                        <tr key={idx} data-testid={`contact-row-${idx}`} className="hover:bg-slate-50">
                          <td className="whitespace-nowrap px-4 py-3 text-slate-600">{fmtDate(c.created_at)}</td>
                          <td className="px-4 py-3 font-medium text-[#0a2540]">{c.name}</td>
                          <td className="px-4 py-3">{c.pharmacy}</td>
                          <td className="px-4 py-3 text-slate-600">{c.email}</td>
                          <td className="px-4 py-3 text-slate-600">{c.phone || "-"}</td>
                          <td className="max-w-md px-4 py-3 text-slate-600">{c.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              <TabsContent value="hub">
                <HubManager />
              </TabsContent>

              <TabsContent value="slides">
                <SlidesManager />
              </TabsContent>

              <TabsContent value="koppelingen">
                <KoppelingenTab />
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
